import { useRef, useState } from 'react';
import type { PlanDto } from '@/api/generated';
import {
  useDeletePlan,
  useDownloadPlan,
  usePlanFileUrl,
  usePlans,
  useUploadPlanVersion,
} from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { Icon, type IconName } from '@/design-system/icon/Icon';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { ConfirmDialog } from '@/design-system/primitives/Dialog/ConfirmDialog';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { PlanCreateDialog, PlanEditDialog } from './PlanDialogs';
import styles from './Plans.module.css';

const DISCIPLINE_LABEL = {
  Arch: 'Architectuur',
  Tech: 'Technieken',
  Stab: 'Stabiliteit',
} as const;

const DISCIPLINE_ICON: Record<'Arch' | 'Tech' | 'Stab', IconName> = {
  Arch: 'arch',
  Tech: 'tool',
  Stab: 'shield',
};

function PlanSection({
  title,
  icon,
  items,
  selectedId,
  onSelect,
  onEdit,
  onDelete,
  onDownload,
}: {
  title: string;
  icon: IconName;
  items: PlanDto[];
  selectedId: string | null;
  onSelect: (p: PlanDto) => void;
  onEdit: (p: PlanDto) => void;
  onDelete: (p: PlanDto) => void;
  onDownload: (p: PlanDto) => void;
}) {
  if (items.length === 0) return null;
  return (
    <>
      <div className={`eyebrow ${styles.indexLabel}`}>{title}</div>
      {items.map((p) => (
        <PlanRow
          key={p.id}
          plan={p}
          icon={icon}
          isSelected={p.id === selectedId}
          onSelect={onSelect}
          onEdit={onEdit}
          onDelete={onDelete}
          onDownload={onDownload}
        />
      ))}
    </>
  );
}

function PlanRow({
  plan,
  icon,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
  onDownload,
}: {
  plan: PlanDto;
  icon: IconName;
  isSelected: boolean;
  onSelect: (p: PlanDto) => void;
  onEdit: (p: PlanDto) => void;
  onDelete: (p: PlanDto) => void;
  onDownload: (p: PlanDto) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  // BF-8 — only uploaded plans (BF-1 backend) have a downloadable blob.
  // Metadata-only plans (legacy) hide the download item.
  const hasFile = Boolean(plan.sha256);
  return (
    <div
      className={`${styles.indexItem} ${isSelected ? styles.indexItemActive : ''}`}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={`Toon ${plan.name} in de viewer`}
      onClick={() => onSelect(plan)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(plan);
        }
      }}
    >
      <div className="row gap-6">
        <Icon name={icon} size={12} color={isSelected ? 'var(--accent-2)' : 'var(--ink-3)'} />
        <span className={`${styles.indexName} ${isSelected ? styles.indexNameActive : ''}`}>
          {plan.name}
        </span>
        <span
          className={`mono ml-auto ${styles.indexVersion} ${plan.approved ? styles.versionSigned : ''}`}
        >
          {plan.version}
          {plan.approved ? ' ✓' : ''}
        </span>
        <div
          className={styles.kebabWrap}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className={styles.kebabBtn}
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={`Meer acties voor ${plan.name}`}
          >
            <Icon name="more" size={12} />
          </button>
          {menuOpen && (
            <>
              <button
                type="button"
                className={styles.kebabBackdrop}
                aria-label="Sluit menu"
                tabIndex={-1}
                onClick={() => setMenuOpen(false)}
              />
              <div role="menu" className={styles.kebabMenu}>
                {hasFile && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      onDownload(plan);
                    }}
                  >
                    Download PDF
                  </button>
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit(plan);
                  }}
                >
                  Bewerk
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="c-bad"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete(plan);
                  }}
                >
                  Verwijder
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

type ViewMode = 'one' | 'two' | 'overlay';

export function Plans() {
  const plans = usePlans({ pageSize: 100 });
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PlanDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PlanDto | null>(null);
  // Client-side selection — clicking a row in the index sets which plan is
  // shown in the viewer. Initially falls back to whichever plan the backend
  // marks `active: true` (kept as the boot-time default so deep links and
  // first visits land on something), but every subsequent change is local.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Compare-mode: 'one' = single viewer (default); 'two' = primary + secondary
  // side-by-side; 'overlay' = secondary stacked on top of primary at 50%
  // opacity. Secondary is derived from the list (next plan after primary)
  // so the user only manages one selection.
  const [viewMode, setViewMode] = useState<ViewMode>('one');
  // User-pinned secondary; falls back to the next plan in the list when
  // null or when it points to the same plan as primary.
  const [secondaryId, setSecondaryId] = useState<string | null>(null);
  const deleteMutation = useDeletePlan();
  const downloadPlan = useDownloadPlan();
  const handleDownload = (plan: PlanDto) => {
    downloadPlan.mutate({
      id: plan.id,
      filename: plan.fileName ?? `${plan.name}.pdf`,
    });
  };

  return (
    <AppFrame>
      <QueryBoundary query={plans} loading="Plannen laden…">
        {(page) => {
          const arch = page.items.filter((p) => p.discipline === 'Arch');
          const tech = page.items.filter((p) => p.discipline === 'Tech');
          const stab = page.items.filter((p) => p.discipline === 'Stab');
          const serverActive = page.items.find((p) => p.active);
          const effectiveSelectedId = selectedId ?? serverActive?.id ?? null;
          const primary =
            effectiveSelectedId === null
              ? null
              : (page.items.find((p) => p.id === effectiveSelectedId) ?? null);
          // Secondary: user's pick if it exists AND differs from primary,
          // else the next plan in the list. Null when there's only one plan.
          const secondary: PlanDto | null = (() => {
            if (viewMode === 'one') return null;
            const pinned = secondaryId
              ? page.items.find((p) => p.id === secondaryId)
              : undefined;
            if (pinned && pinned.id !== primary?.id) return pinned;
            return page.items.find((p) => p.id !== primary?.id) ?? null;
          })();
          // Plans available for the secondary picker — everything except
          // the primary. Used as <option> list in 'two' / 'overlay' modes.
          const secondaryOptions = page.items.filter((p) => p.id !== primary?.id);

          return (
            <>
              <div className={`row ${styles.headerRow}`}>
                <div>
                  <div className="eyebrow">
                    Plannen · {page.items.length} plannen · 3 disciplines
                  </div>
                  <h1 className={styles.title}>Plannen-viewer</h1>
                </div>
                <div className={`ml-auto row gap-8 ${styles.headerActions}`}>
                  <div
                    className={`row gap-4 ${styles.segment}`}
                    role="tablist"
                    aria-label="Viewer-modus"
                  >
                    <Btn
                      variant="ghost"
                      role="tab"
                      aria-selected={viewMode === 'one'}
                      className={`${styles.segmentBtn} ${
                        viewMode === 'one' ? styles.segmentActive : ''
                      }`}
                      onClick={() => setViewMode('one')}
                    >
                      1 plan
                    </Btn>
                    <Btn
                      variant="ghost"
                      role="tab"
                      aria-selected={viewMode === 'two'}
                      className={`${styles.segmentBtn} ${
                        viewMode === 'two' ? styles.segmentActive : ''
                      }`}
                      onClick={() => setViewMode('two')}
                      disabled={page.items.length < 2}
                      title={
                        page.items.length < 2
                          ? 'Voeg een tweede plan toe om te vergelijken'
                          : undefined
                      }
                    >
                      2 naast elkaar
                    </Btn>
                    <Btn
                      variant="ghost"
                      role="tab"
                      aria-selected={viewMode === 'overlay'}
                      className={`${styles.segmentBtn} ${
                        viewMode === 'overlay' ? styles.segmentActive : ''
                      }`}
                      onClick={() => setViewMode('overlay')}
                      disabled={page.items.length < 2}
                      title={
                        page.items.length < 2
                          ? 'Voeg een tweede plan toe om te vergelijken'
                          : undefined
                      }
                    >
                      Overlay
                    </Btn>
                  </div>
                  <Btn variant="primary" onClick={() => setCreateOpen(true)}>
                    <Icon name="plus" size={14} /> Plan toevoegen
                  </Btn>
                </div>
              </div>
              <PlanCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
              {editTarget && (
                <PlanEditDialog
                  open={editTarget !== null}
                  onOpenChange={(o) => {
                    if (!o) setEditTarget(null);
                  }}
                  plan={editTarget}
                />
              )}
              <ConfirmDialog
                open={deleteTarget !== null}
                onOpenChange={(o) => {
                  if (!o) setDeleteTarget(null);
                }}
                title={`Plan ${deleteTarget?.name ?? ''} verwijderen?`}
                description="Het plan-record wordt definitief verwijderd."
                confirmLabel="Verwijder"
                destructive
                pending={deleteMutation.isPending}
                onConfirm={async () => {
                  if (!deleteTarget) return;
                  try {
                    await deleteMutation.mutateAsync(deleteTarget.id);
                    setDeleteTarget(null);
                  } catch {
                    // toast surfaced by hook
                  }
                }}
              />

              <div className={styles.body}>
                <Card className={styles.index}>
                  <PlanSection
                    title={DISCIPLINE_LABEL.Arch}
                    icon={DISCIPLINE_ICON.Arch}
                    items={arch}
                    selectedId={effectiveSelectedId}
                    onSelect={(p) => setSelectedId(p.id)}
                    onEdit={setEditTarget}
                    onDelete={setDeleteTarget}
                    onDownload={handleDownload}
                  />
                  <PlanSection
                    title={DISCIPLINE_LABEL.Tech}
                    icon={DISCIPLINE_ICON.Tech}
                    items={tech}
                    selectedId={effectiveSelectedId}
                    onSelect={(p) => setSelectedId(p.id)}
                    onEdit={setEditTarget}
                    onDelete={setDeleteTarget}
                    onDownload={handleDownload}
                  />
                  <PlanSection
                    title={DISCIPLINE_LABEL.Stab}
                    icon={DISCIPLINE_ICON.Stab}
                    items={stab}
                    selectedId={effectiveSelectedId}
                    onSelect={(p) => setSelectedId(p.id)}
                    onEdit={setEditTarget}
                    onDelete={setDeleteTarget}
                    onDownload={handleDownload}
                  />
                </Card>

                {viewMode === 'one' && (
                  <Card className={styles.viewer}>
                    <PlanViewerToolbar plan={primary} onDownload={handleDownload} />
                    <PlanViewerCanvas plan={primary} />
                  </Card>
                )}

                {viewMode === 'two' && (
                  <div className={styles.viewerPair}>
                    <Card className={styles.viewer}>
                      <PlanViewerToolbar plan={primary} onDownload={handleDownload} />
                      <PlanViewerCanvas plan={primary} />
                    </Card>
                    <Card className={styles.viewer}>
                      <PlanViewerToolbar
                        plan={secondary}
                        onDownload={handleDownload}
                        planOptions={secondaryOptions}
                        onPickPlan={setSecondaryId}
                      />
                      <PlanViewerCanvas plan={secondary} />
                    </Card>
                  </div>
                )}

                {viewMode === 'overlay' && (
                  <Card className={styles.viewer}>
                    <PlanViewerToolbar
                      plan={primary}
                      onDownload={handleDownload}
                      overlayPlan={secondary}
                      planOptions={secondaryOptions}
                      onPickPlan={setSecondaryId}
                    />
                    <div className={styles.overlayContainer}>
                      <PlanViewerCanvas plan={primary} variant="overlayBase" />
                      <PlanViewerCanvas plan={secondary} variant="overlayTop" />
                    </div>
                  </Card>
                )}
              </div>
            </>
          );
        }}
      </QueryBoundary>
    </AppFrame>
  );
}

// Viewer toolbar — kept in its own component so the version-upload hook
// captures the currently active plan's id consistently across renders.
// When no plan is active the "Nieuwe versie" button is hidden along with
// the download.
//
// In compare modes the toolbar gets a small <select> so the user can pick
// which plan this pane shows. For the overlay viewer the toolbar belongs
// to the base plan; `overlayPlan` adds an inline label + picker for the
// stacked second plan so both stay visible from one toolbar.
function PlanViewerToolbar({
  plan,
  onDownload,
  planOptions,
  onPickPlan,
  overlayPlan,
}: {
  plan: PlanDto | null;
  onDownload: (p: PlanDto) => void;
  planOptions?: PlanDto[];
  onPickPlan?: (id: string) => void;
  overlayPlan?: PlanDto | null;
}) {
  // Hooks always run; the mutation captures whichever plan is active. The
  // file input is a hidden trigger driven by the visible button so the
  // styling stays consistent with the rest of the toolbar.
  const uploadVersion = useUploadPlanVersion(plan?.id ?? '');
  const fileInput = useRef<HTMLInputElement | null>(null);

  const onPickNewVersion = () => fileInput.current?.click();
  const onNewVersionChosen = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !plan) return;
    try {
      await uploadVersion.mutateAsync(file);
    } catch {
      // toast surfaced by the hook
    }
  };

  const hasPicker = planOptions !== undefined && onPickPlan !== undefined;

  return (
    <div className={`row gap-12 ${styles.viewerToolbar}`}>
      {hasPicker && planOptions.length > 0 ? (
        <select
          className={styles.viewerPicker}
          value={plan?.id ?? ''}
          onChange={(e) => onPickPlan(e.target.value)}
          aria-label="Kies plan voor deze viewer"
        >
          {plan === null && <option value="">Kies een plan…</option>}
          {planOptions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {p.version}
            </option>
          ))}
        </select>
      ) : (
        <span className={styles.viewerTitle}>
          {plan ? `${plan.name} — ${plan.version}` : 'Geen plan geselecteerd'}
        </span>
      )}
      {plan?.approved && (
        <Pill kind="ok" dot={false}>
          goedgekeurd
        </Pill>
      )}
      {overlayPlan !== undefined && (
        <>
          <span className={styles.overlayJoin}>+ overlay</span>
          {planOptions && planOptions.length > 0 && onPickPlan ? (
            <select
              className={styles.viewerPicker}
              value={overlayPlan?.id ?? ''}
              onChange={(e) => onPickPlan(e.target.value)}
              aria-label="Kies overlay-plan"
            >
              {overlayPlan === null && <option value="">Kies een plan…</option>}
              {planOptions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.version}
                </option>
              ))}
            </select>
          ) : (
            <span className={styles.viewerTitle}>
              {overlayPlan ? `${overlayPlan.name} — ${overlayPlan.version}` : '—'}
            </span>
          )}
        </>
      )}
      <div className={`ml-auto row gap-4 ${styles.viewerControls}`}>
        {plan?.sha256 && (
          <Btn variant="ghost" onClick={() => onDownload(plan)}>
            <Icon name="download" size={13} /> Download
          </Btn>
        )}
        {plan && (
          <Btn onClick={onPickNewVersion} disabled={uploadVersion.isPending}>
            <Icon name="upload" size={13} />{' '}
            {uploadVersion.isPending ? 'Uploaden…' : 'Nieuwe versie'}
          </Btn>
        )}
        <input
          ref={fileInput}
          type="file"
          accept="application/pdf,image/*"
          style={{ display: 'none' }}
          onChange={onNewVersionChosen}
        />
      </div>
    </div>
  );
}

// BF-8 → 2026-05-16 follow-up — replace the hardcoded SVG floor-plan
// placeholder with a real renderer for the active plan's uploaded blob.
// PDF goes through <embed>; everything else (PNG/JPG/SVG) renders as <img>.
// When the plan has no blob, surface a clear empty-state instead of fake
// content so the user knows where to go next.
function PlanViewerCanvas({
  plan,
  variant,
}: {
  plan: PlanDto | null;
  // `overlayBase` and `overlayTop` add absolute-positioning so two canvases
  // can be stacked inside the same parent. `overlayTop` also drops opacity
  // and disables pointer events so the base stays interactive underneath.
  variant?: 'overlayBase' | 'overlayTop';
}) {
  // Hook must be called unconditionally; pass undefined to keep `enabled: false`.
  const fileUrl = usePlanFileUrl(plan?.sha256 ? plan.id : undefined);

  const canvasClass = `${styles.canvas} ${
    variant === 'overlayBase'
      ? styles.canvasOverlayBase
      : variant === 'overlayTop'
        ? styles.canvasOverlayTop
        : ''
  }`;

  if (!plan) {
    return (
      <div className={canvasClass}>
        <div className={styles.emptyState}>
          <h3 className={styles.emptyTitle}>Geen plan geselecteerd</h3>
          <p className={styles.emptyBody}>
            Voeg een plan toe via "Plan toevoegen" — optioneel met een PDF zodat het hier rendert.
          </p>
        </div>
      </div>
    );
  }

  if (!plan.sha256) {
    return (
      <div className={canvasClass}>
        <div className={styles.emptyState}>
          <h3 className={styles.emptyTitle}>Geen PDF bijgevoegd</h3>
          <p className={styles.emptyBody}>
            Dit plan is alleen een metadata-record. Bewerk het plan en upload een PDF om hem hier te
            bekijken.
          </p>
        </div>
      </div>
    );
  }

  if (fileUrl.isPending) {
    return (
      <div className={canvasClass}>
        <div className={styles.emptyState}>
          <p className={styles.emptyBody}>Plan laden…</p>
        </div>
      </div>
    );
  }

  if (fileUrl.isError || !fileUrl.data) {
    return (
      <div className={canvasClass}>
        <div className={styles.emptyState}>
          <h3 className={styles.emptyTitle}>Plan kon niet geladen worden</h3>
          <p className={styles.emptyBody}>
            Controleer of het bestand nog op de server staat of probeer opnieuw te uploaden.
          </p>
        </div>
      </div>
    );
  }

  const isPdf =
    (plan.mime ?? '').toLowerCase().includes('pdf') ||
    (plan.fileName ?? '').toLowerCase().endsWith('.pdf');

  return (
    <div className={canvasClass}>
      {isPdf ? (
        <embed
          src={fileUrl.data}
          type="application/pdf"
          title={plan.name}
          className={styles.planEmbed}
        />
      ) : (
        // The legacy fake-SVG canvas + tooltip block lived here; replaced
        // with a real image renderer for non-PDF uploads.
        <img src={fileUrl.data} alt={plan.name} className={styles.planImage} />
      )}
    </div>
  );
}
